import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';
import { AtSymbolIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Write something...",
  className = "",
  maxLength = 10000,
  showMentionButton = true,
  error = null
}) => {
  const [showMentionModal, setShowMentionModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [focused, setFocused] = useState(false);
  const quillRef = useRef(null);

  // Load users and departments for mentions
  useEffect(() => {
    const loadMentionData = async () => {
      try {
        const [usersResponse, departmentsResponse] = await Promise.all([
          api.get('/auth/users'),
          api.get('/departments')
        ]);
        
        setUsers(usersResponse.data.users || []);
        setDepartments(departmentsResponse.data.departments || []);
      } catch (error) {
        console.error('Error loading mention data:', error);
      }
    };
    
    loadMentionData();
  }, []);

  // Custom toolbar configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['blockquote', 'code-block'],
        [{ 'align': [] }],
        ['link'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'blockquote', 'code-block',
    'align',
    'link'
  ];

  // Improved mention detection logic
  const handleChange = (content, delta, source, editor) => {
    const text = editor.getText();
    
    // Check for @ mentions only when user is typing
    if (source === 'user') {
      const selection = editor.getSelection();
      if (selection) {
        const cursorPosition = selection.index;
        const textBeforeCursor = text.substring(0, cursorPosition);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');
        
        if (lastAtIndex !== -1) {
          const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
          
          // Check if we're still in a mention (no spaces, newlines, or punctuation after @)
          const mentionPattern = /^[a-zA-Z0-9_]*$/;
          if (mentionPattern.test(textAfterAt) && textAfterAt.length <= 20) {
            const query = textAfterAt.toLowerCase();
            
            // Filter suggestions
            const suggestions = [
              ...users
                .filter(user => {
                  const userName = user.name.replace(/\s+/g, '').toLowerCase();
                  const userEmail = user.email.toLowerCase();
                  return userName.includes(query) || userEmail.includes(query);
                })
                .map(user => ({
                  type: 'user',
                  id: user.id,
                  name: user.name,
                  email: user.email,
                  display: user.name,
                  mention: `@${user.name.replace(/\s+/g, '')}`
                })),
              ...departments
                .filter(dept => {
                  const deptName = dept.name.replace(/\s+/g, '').toLowerCase();
                  return deptName.includes(query);
                })
                .map(dept => ({
                  type: 'department',
                  id: dept.id,
                  name: dept.name,
                  display: dept.name,
                  mention: `@${dept.name.replace(/\s+/g, '')}`
                }))
            ].slice(0, 8);
            
            setMentionSuggestions(suggestions);
            setShowSuggestions(suggestions.length > 0 && textAfterAt.length > 0);
            setSelectedIndex(0);
          } else {
            setShowSuggestions(false);
          }
        } else {
          setShowSuggestions(false);
        }
      }
    }
    
    onChange(content);
  };

  // Insert mention at cursor with proper spacing
  const insertMention = (mention) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const text = quill.getText();
    const cursorPosition = selection.index;
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Calculate the length of text to replace (@ + partial text)
      const mentionLength = cursorPosition - lastAtIndex;
      
      // Delete the @ and partial text
      quill.deleteText(lastAtIndex, mentionLength);
      
      // Insert mention with special formatting
      quill.insertText(lastAtIndex, mention.mention, {
        'background': '#E0E7FF',
        'color': '#3730A3',
        'bold': true
      });
      
      // Add a space after the mention and reset formatting
      const mentionEndPosition = lastAtIndex + mention.mention.length;
      quill.insertText(mentionEndPosition, ' ', {
        'background': false,
        'color': false,
        'bold': false
      });
      
      // Set cursor after the mention and space
      quill.setSelection(mentionEndPosition + 1);
    }

    setShowSuggestions(false);
  };

  // Handle keyboard navigation for mentions
  const handleKeyDown = (e) => {
    if (showSuggestions && mentionSuggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < mentionSuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : mentionSuggestions.length - 1
          );
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (mentionSuggestions[selectedIndex]) {
            insertMention(mentionSuggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          break;
        case ' ':
          // Close suggestions when space is pressed
          setShowSuggestions(false);
          break;
        default:
          break;
      }
    }
  };

  const openMentionModal = () => {
    setShowMentionModal(true);
  };

  const handleMentionSelect = (mention) => {
    insertMention(mention);
    setShowMentionModal(false);
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Rich Text Editor */}
      <div 
        className={`rich-text-editor ${focused ? 'focused' : ''} ${error ? 'error' : ''}`}
        onKeyDown={handleKeyDown}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
          style={{
            minHeight: '120px'
          }}
        />
      </div>

      {/* Mention Button */}
      {showMentionButton && (
        <button
          type="button"
          onClick={openMentionModal}
          className="absolute bottom-3 right-3 p-2 text-gray-400 hover:text-indigo-600 transition-colors z-10"
          title="Add mention"
        >
          <AtSymbolIcon className="h-5 w-5" />
        </button>
      )}

      {/* Character Count */}
      {maxLength && (
        <div className="mt-1 text-xs text-gray-500 text-right">
          {value ? value.replace(/<[^>]*>/g, '').length : 0}/{maxLength} characters
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Mention Suggestions Dropdown */}
      {showSuggestions && mentionSuggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-64 mention-suggestions">
          {mentionSuggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.type}-${suggestion.id}`}
              onClick={() => insertMention(suggestion)}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
            >
              <div className="flex items-center">
                <div className={`suggestion-type-indicator suggestion-type-${suggestion.type}`} />
                <div>
                  <div className="suggestion-name">
                    {suggestion.display}
                  </div>
                  {suggestion.email && (
                    <div className="suggestion-email">{suggestion.email}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mention Modal */}
      {showMentionModal && (
        <MentionModal
          users={users}
          departments={departments}
          onClose={() => setShowMentionModal(false)}
          onMentionSelect={handleMentionSelect}
        />
      )}
    </div>
  );
};

// Simple Mention Modal Component
const MentionModal = ({ users, departments, onClose, onMentionSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSuggestions = [
    ...users
      .filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .map(user => ({
        type: 'user',
        id: user.id,
        name: user.name,
        email: user.email,
        display: user.name,
        mention: `@${user.name.replace(/\s+/g, '')}`
      })),
    ...departments
      .filter(dept => 
        dept.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .map(dept => ({
        type: 'department',
        id: dept.id,
        name: dept.name,
        display: dept.name,
        mention: `@${dept.name.replace(/\s+/g, '')}`
      }))
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-96">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Add Mention</h3>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users or departments..."
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredSuggestions.map((suggestion) => (
            <button
              key={`${suggestion.type}-${suggestion.id}`}
              type="button"
              onClick={() => onMentionSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3"
            >
              <div className={`w-3 h-3 rounded-full ${
                suggestion.type === 'user' ? 'bg-indigo-500' : 'bg-blue-500'
              }`} />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {suggestion.display}
                </div>
                {suggestion.email && (
                  <div className="text-xs text-gray-500">{suggestion.email}</div>
                )}
              </div>
            </button>
          ))}
          {filteredSuggestions.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500">
              No users or departments found
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor; 